import { Layer } from "@carbon/react";

import { ProgressStepModal, useProgressStepModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer";
import { mapToModalError } from "src/errors";
import { getRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery";
import { CreateCasePropertiesRule } from "src/graphql/anti-fraud/queries/rules/mutations/CreateCasePropertiesRuleMutation";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { mapToApiOperatorCode } from "src/mappers/operator.mappers";
import { Rule } from "src/types/rule";
import { cn } from "src/utils/string.utils";

import { DetailsForm, DetailsFormData } from "../DetailsForm";
import { useDetailsForm } from "../DetailsForm/useDetailsForm";
import { CaseTypeSelection } from "../linkedCase/CaseTypeSelection/CaseTypeSelection";
import { useCaseTypeSelection } from "../linkedCase/CaseTypeSelection/useCaseTypeSelection";

import { SimpleRuleDefinition } from "./SimpleRuleDefinition";
import { useSimpleRuleDefinition } from "./useSimpleRuleDefinition";

type CreateSimpleRuleModalProps = {
  onClose: () => void;
  caseTypeCode: string;
  rules: Rule[];
};

export const CreateSimpleRuleModal: React.FC<CreateSimpleRuleModalProps> = ({
  onClose,
  caseTypeCode,
  rules,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();

  const simpleRuleDefinitionControl = useSimpleRuleDefinition();
  const caseTypeSelectionControl = useCaseTypeSelection();
  const detailsFormControl = useDetailsForm({ rules });

  const {
    performMutation: createSimpleRule,
    loading,
    error,
  } = usePerformMutation(CreateCasePropertiesRule, {
    onSuccess: () => {
      showSuccessToast(t("page.rules.modal.create.toast.success.message"));
      onClose();
    },
    mutationOption: {
      refetchQueries: [getRulesQuery],
      awaitRefetchQueries: true,
    },
  });

  const submit = async (data: DetailsFormData) => {
    const criteriaInput = simpleRuleDefinitionControl.criteria.map((criteria) => ({
      ...criteria,
      operator: criteria.operator
        ? {
            code: mapToApiOperatorCode(criteria.operator.code),
            valueType: criteria.operator.valueType,
          }
        : null,
    }));
    const valueConditions = criteriaInput.flatMap((criteria) =>
      criteria.operator && criteria.operator.valueType !== "property"
        ? [{ ...criteria, operator: { ...criteria.operator } }]
        : []
    );
    const propertyConditions = criteriaInput.flatMap((criteria) =>
      criteria.operator && criteria.operator.valueType === "property"
        ? [{ ...criteria, operator: { ...criteria.operator } }]
        : []
    );

    await createSimpleRule({
      name: data.name,
      code: data.code,
      description: data.description,
      caseTypeCode: caseTypeCode,
      valueConditions: valueConditions.map((criteria) => {
        return {
          propertyPath: {
            elementPath: criteria.property?.elementCode ? [criteria.property.elementCode] : [],
            propertyCode: criteria.property?.code || "",
          },
          operatorCode: criteria.operator.code,
          value: criteria.value,
          listCondition: null,
        };
      }),
      propertyConditions: propertyConditions.map((criteria) => ({
        operatorCode: criteria.operator.code,
        propertyPath: {
          elementPath: criteria.property?.elementCode ? [criteria.property.elementCode] : [],
          propertyCode: criteria.property?.code || "",
        },
        comparisonProperty: {
          elementPath: criteria.property?.elementCode ? [criteria.property.elementCode] : [],
          propertyCode: criteria.property?.code || "",
        },
      })),
    });
  };

  const { control: stepsControl, modalProps } = useProgressStepModal([
    {
      title: t("page.rules.simple.modal.create.title"),
      type: "criteria",
      invalid: simpleRuleDefinitionControl.hasError,
      disabled: false,
      submit: simpleRuleDefinitionControl.submit(() => stepsControl.goNextStep()),
      focus: "#criteria",
    },
    {
      title: t("page.rules.modal.create.linkedCase.form.caseType.title"),
      type: "caseType",
      invalid: caseTypeSelectionControl.hasError,
      disabled: false,
      submit: caseTypeSelectionControl.submit(() => {
        stepsControl.goNextStep();
      }),
      focus: "#case-type-selection",
    },
    {
      title: t("page.rules.modal.create.linkedCase.form.details.title"),
      type: "details",
      invalid: detailsFormControl.hasError,
      disabled: false,
      submit: detailsFormControl.submit(submit),
      focus: "#name",
    },
  ]);

  return (
    <ProgressStepModal
      loading={loading}
      kind="vertical"
      error={mapToModalError(error)}
      initialFocus="#criteria"
      title={t("page.rules.simple.button.create")}
      confirmButtonText={t("general.actions.create")}
      onClose={onClose}
      {...modalProps}
    >
      <Layer level={1} className="flex flex-col h-full min-h-[540px] bg-layer01">
        <div
          className={cn("flex flex-col h-full px-5", {
            hidden: !stepsControl.isStep("criteria"),
          })}
        >
          <SimpleRuleDefinition
            caseTypeCode={caseTypeCode}
            methods={simpleRuleDefinitionControl.methods}
          />
        </div>
        <div
          className={cn("flex flex-col h-full px-5", {
            hidden: !stepsControl.isStep("caseType"),
          })}
        >
          <CaseTypeSelection methods={caseTypeSelectionControl.methods} />
        </div>
        <div
          className={cn("flex flex-col px-5 h-full", {
            hidden: !stepsControl.isStep("details"),
          })}
        >
          <DetailsForm
            detailsFormControl={detailsFormControl}
            selectedCaseTypes={
              caseTypeSelectionControl.selectedCaseType
                ? [caseTypeSelectionControl.selectedCaseType]
                : []
            }
          />
        </div>
      </Layer>
    </ProgressStepModal>
  );
};
